export const customerRelationshipOptions = {
    label: {
        isBillTo: "Is a bill-to customer",
        isShipTo: "Is a ship-to customer",
        isNotBillTo: "Is not a bill-to customer",
        isNotShipTo: "Is not a ship-to customer",
    },
    value: {
        isBillTo: "billTo",
        isShipTo: "shipTo",
        isNotBillTo: "notBillTo",
        isNotShipTo: "notShipTo",
    },
};

export const customerExposuresOptions = {
    label: {
        missingCertificates: "Missing certificates",
        invalidCertificates: "Invalid certificates",
        expiredCertificates: "Expired certificates",
    },
    value: {
        missingCertificates: "EXPOSURE: MISSING CERT",
        invalidCertificates: "EXPOSURE: INVALID CERT",
        expiredCertificates: "EXPOSURE: EXPIRED CERT",
    },
};

/**
 * Splits newline-separated identifier string into array, trims each value, filters empty, and maps with processor.
 * @param {string | undefined} ids - Newline-separated string of identifiers
 * @param {function} [processor=v => v] - Optional function to transform each value
 * @returns {Array} Processed array of values
 */
export const processIdentifiers = (ids, processor = v => v) => {
    if (!ids?.length) return [];
    return ids
        .split("\n")
        .map(value => value.trim())
        .filter(value => value !== "")
        .map(processor);
};

// Internal-only filter state keys that never produce filter chips on their own.
// - Date range suffixes (DateMode, FromDaysAgo, ToDaysAgo, ToDirection) drive
//   the Exact/Relative toggle and "days ago" inputs; the canonical
//   DateAfter/DateBefore fields they back are already chip-able.
// - The "Error" suffix covers field-level validation error state (e.g.
//   certificateIdError, customerCodeError). These are inline-validation
//   messages held on the filter slice next to the real filter value and must
//   not surface as removable chips in the "Filtered by" bar (CERTCAP-71981).
export const EXCLUDED_KEY_SUFFIXES = [
    "DateMode",
    "FromDaysAgo",
    "ToDaysAgo",
    "ToDirection",
    "Error",
];

/**
 * Determines whether a filter state key should be excluded from chip rendering.
 * A key is excluded when it matches an explicit excluded key or ends with one
 * of the internal date range filter suffixes.
 * @param {string} key - The filter state key to evaluate
 * @param {string[]} [excludedKeys=[]] - Explicit keys to exclude
 * @returns {boolean} True if the key should be excluded from chips
 */
export const isExcludedFilterKey = (key, excludedKeys = []) => {
    if (excludedKeys.includes(key)) {
        return true;
    }
    return EXCLUDED_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));
};

/**
 * Returns the relative-date input state entries to reset when a *DateAfter
 * or *DateBefore filter chip is dismissed, but only when the date range is
 * currently in relative mode.
 *
 * DateRangeFilter stores relative-mode inputs (FromDaysAgo, ToDaysAgo,
 * ToDirection) alongside the canonical date fields. Each side is independent —
 * dismissing one chip resets only that side's input so the other side and the
 * mode toggle remain unaffected.
 *
 * @param {string} key - The dismissed filter state key (e.g. "certificateCreatedDateAfter")
 * @param {Record<string, unknown>} defaults - The filter defaults object to read reset values from
 * @param {Record<string, unknown>} currentState - The live filter state used to check the current date mode
 * @returns {Array<{key: string, value: unknown}>} Key/value pairs to dispatch individually
 */
export function getRelativeDateResets(key, defaults, currentState) {
    const isDateAfter = key.endsWith("DateAfter");
    const isDateBefore = key.endsWith("DateBefore");

    if (!isDateAfter && !isDateBefore) return [];

    const prefix = isDateAfter
        ? key.slice(0, -"DateAfter".length)
        : key.slice(0, -"DateBefore".length);

    if (currentState[`${prefix}DateMode`] !== "relative") return [];

    const companions = [];

    const addIfExists = companionKey => {
        if (Object.prototype.hasOwnProperty.call(defaults, companionKey)) {
            companions.push({ key: companionKey, value: defaults[companionKey] });
        }
    };

    if (isDateAfter) {
        addIfExists(`${prefix}FromDaysAgo`);
    } else {
        addIfExists(`${prefix}ToDaysAgo`);
        addIfExists(`${prefix}ToDirection`);
    }

    return companions;
}

export function getCustomerRelationShip(relationShip, key) {
    const oppositeKey =
        key === customerRelationshipOptions.value.isBillTo
            ? customerRelationshipOptions.value.isNotBillTo
            : customerRelationshipOptions.value.isNotShipTo;

    // If both positive and negative relationships exist, return null
    if (relationShip.includes(key) && relationShip.includes(oppositeKey)) {
        return null;
    }

    // Return true if key exists, false if opposite exists, null if neither exists
    return relationShip.includes(key) ? true : relationShip.includes(oppositeKey) ? false : null;
}
